import axios from 'axios'
import { startOfDay, endOfDay } from 'date-fns'

import { mapActivityLogEntry } from '@/api/clients/get-client-activities'
import { getAuthApiUrl, getCookie, setAuthCookie } from '@/helpers/helpers'
import i18n from '@/i18n'
import { getTokenFromURL } from '@/utils'
import { clearImpersonation, toAuthRoleCookie } from '@/utils/auth'

import { getPackagesWithCountQueryParams, getPackagesCSVQueryParams } from './helpers'

const param = (obj, param = '', withParsedArrayValues) => {
  const formData = new FormData()
  if (Array.isArray(obj)) {
    formData.append(param, obj)
  } else {
    const filesKeys = Object.keys(obj).filter(
      (key) => (Array.isArray(obj[key]) && obj[key][0] instanceof Blob) || obj[key] instanceof Blob,
    )
    const valuesKeys = Object.keys(obj).filter((key) => !filesKeys.includes(key))

    valuesKeys.forEach(function (key) {
      if (Array.isArray(obj[key])) {
        if (withParsedArrayValues) {
          obj[key].map((x, i) => formData.append(key, obj[key][i]))
        } else {
          formData.append(key, JSON.stringify(obj[key]))
        }
      } else {
        formData.append(key, obj[key])
      }
    })

    filesKeys.forEach(function (key) {
      if (Array.isArray(obj[key])) {
        obj[key].map((x, i) => formData.append(key, obj[key][i]))
      } else if (obj[key] instanceof Blob) {
        const fileName = obj[key].name || 'image.png'
        formData.append(key, obj[key], fileName)
      }
    })
  }
  return formData
}

export default {
  getDigitalSignageLocation: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/tv-app/buildingId/${buildingId}/locations`, {
      headers: {
        token: token,
      },
    })
  },

  deleteDigitalSignageLocation: (locationId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/tv-app/locationid/${locationId}`, {
      headers: {
        token: token,
      },
    })
  },

  createDigitalSignageLocation: (buildingId, locationName) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/tv-app/buildingId/${buildingId}/create-location`,
      {
        locationName,
      },
      {
        headers: {
          token: token,
        },
      },
    )
  },

  updateDigitalSignageLocationName: (locationId, locationName) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/tv-app/locationId/${locationId}/name`,
      {
        locationName,
      },
      {
        headers: {
          token: token,
        },
      },
    )
  },

  getUserToken: function (email, password, fromLogin = false) {
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${getAuthApiUrl()}/session`)
      req.setRequestHeader('Content-type', 'application/x-www-form-urlencoded')
      req.onload = () => {
        if (req.status === 201 && req.response) {
          const res = JSON.parse(req.response)
          // A real login always starts a clean session — never inherit a parked support session.
          clearImpersonation()
          setAuthCookie('token', res.token)
          setAuthCookie('role', toAuthRoleCookie(res.role))
          resolve(req.status)
        } else {
          if (fromLogin) {
            reject(req)
          } else {
            reject(req.status)
          }
        }
      }
      req.onerror = () => reject(req.status)
      req.send(`email=${encodeURIComponent(email)}&password=${password}`)
    })
  },

  verifyAuthEmail: (email) => {
    return axios.post(`${getAuthApiUrl()}/user/status`, {
      email,
    })
  },

  verifyCommunityCode: (code) => {
    return axios.post(`${getAuthApiUrl()}/building/code`, {
      code,
    })
  },

  getUserData: () => {
    const token = getCookie('token')
    const role = getCookie('role')
    return new Promise((resolve, reject) => {
      if (!token) reject(new Error('Not Authorized'))
      if (!role) reject(new Error('Not Authorized'))
      const req = new XMLHttpRequest()
      req.open('GET', `${getAuthApiUrl()}/${role.toLowerCase()}`)
      req.setRequestHeader('Content-type', 'application/json')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req)
        }
      }
      req.onerror = () => reject(req)
      req.send()
    })
  },

  getClientRecentActivities: (id, limit, skip, filterValue) => {
    const token = getCookie('token')
    const role = getCookie('role')
    return new Promise((resolve, reject) => {
      if (!token) reject(new Error('Not Authorized'))
      if (!role) reject(new Error('Not Authorized'))
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${import.meta.env.VITE_API_URL}/client/${id}/activity-log?skip=${skip}&limit=${limit}${
          filterValue && filterValue !== 'allActivities' ? `&type=${filterValue}` : ''
        }`,
      )
      req.setRequestHeader('Content-type', 'application/json')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res.history.map((entry, index) => mapActivityLogEntry(entry, skip + index)))
        } else {
          reject(req)
        }
      }
      req.onerror = () => reject(req)
      req.send()
    })
  },

  updateUserData: (userData, id = '', role = 'client') => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    if (!role) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/${role}/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(param(userData))
    })
  },

  deleteClientData: (userId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    if (!role) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/client/${userId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(param(userData))
    })
  },

  getGuests: ({ buildingId, search, skip, limit, filter }) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/client-guest-with-count`, {
      headers: { token: token },
      params: { search, skip, limit, filter, buildingId },
    })
  },

  getVendors: ({ buildingId, search, skip, limit, filter }) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/vendor`, {
      headers: { token: token },
      params: { search, skip, limit, buildingId, filter },
    })
  },

  checkIn: (clients, guests, id) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/checkIn/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 204) {
          resolve()
        } else {
          if (req.response) {
            reject(JSON.parse(req.response))
          } else {
            reject(req.status)
          }
        }
      }
      req.onerror = () => {
        if (req.response) {
          reject(JSON.parse(req.response))
        } else {
          reject(req.status)
        }
      }
      req.send(param({ clients, guests }))
    })
  },

  getActionLogs: (startDate, endDate, buildingId, query, skip = 0) => {
    const token = getCookie('token')
    const role = getCookie('role')
    return new Promise((resolve, reject) => {
      if (!token) reject(new Error('Not Authorized'))
      if (!role) reject(new Error('Not Authorized'))
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${
          import.meta.env.VITE_API_URL
        }/task/${startDate}/${endDate}/${buildingId}/${query}?skip=${skip}&limit=100`,
      )
      req.setRequestHeader('Content-type', 'application/json')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          try {
            resolve(JSON.parse(req.response))
          } catch {
            reject(new Error(i18n.t('action-log:errors.parseFailed')))
          }
        } else {
          reject(new Error(i18n.t('action-log:errors.fetchFailed', { status: req.status })))
        }
      }
      req.onerror = () => reject(new Error(i18n.t('common:errors.generic')))
      req.send()
    })
  },

  createNewShiftNote: (userData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/task`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        try {
          const res = JSON.parse(req.response)
          if (req.status === 201 || req.status === 200) resolve(res)
          else
            reject(
              new Error(
                res?.message ||
                  i18n.t('action-log:errors.createShiftNoteFailed', { status: req.status }),
              ),
            )
        } catch {
          reject(
            new Error(i18n.t('action-log:errors.createShiftNoteFailed', { status: req.status })),
          )
        }
      }
      req.onerror = () => reject(new Error(i18n.t('common:errors.generic')))
      req.send(param(userData))
    })
  },

  updateShiftNote: (shiftNoteId, userData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/task/${shiftNoteId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        try {
          const res = JSON.parse(req.response)
          if (req.status === 200 || req.status === 203) resolve(res)
          else
            reject(
              new Error(
                res?.message ||
                  i18n.t('action-log:errors.updateShiftNoteFailed', { status: req.status }),
              ),
            )
        } catch {
          reject(
            new Error(i18n.t('action-log:errors.updateShiftNoteFailed', { status: req.status })),
          )
        }
      }
      req.onerror = () => reject(new Error(i18n.t('common:errors.generic')))
      req.send(param(userData))
    })
  },

  deleteShiftNote: (shiftNoteId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/task/${shiftNoteId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 || req.status === 204) resolve(req.status)
        else
          reject(
            new Error(i18n.t('action-log:errors.deleteShiftNoteFailed', { status: req.status })),
          )
      }
      req.onerror = () => reject(new Error(i18n.t('common:errors.generic')))
      req.send()
    })
  },

  addGuest: (guestData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/task`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        try {
          const res = JSON.parse(req.response)
          if (req.status === 201 || req.status === 200) resolve(res)
          else
            reject(
              new Error(
                res?.message || i18n.t('action-log:errors.addGuestFailed', { status: req.status }),
              ),
            )
        } catch {
          reject(new Error(i18n.t('action-log:errors.addGuestFailed', { status: req.status })))
        }
      }
      req.onerror = () => reject(new Error(i18n.t('common:errors.generic')))
      req.send(param(guestData))
    })
  },

  createNewUser: (userData) => {
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${getAuthApiUrl()}/client`)
      req.onload = () => {
        if (req.status === 201) resolve(JSON.parse(req.response))
        else reject(JSON.parse(req.response))
      }
      req.onerror = () => reject(req.response ? JSON.parse(req.response) : req.status)
      req.send(param(userData))
    })
  },

  createNewUserByManager: (userData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/manager/client`, userData, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  createNewUserByPropertyManager: (userData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/propertyManager/client`, userData, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  recoverPassword: (email) => {
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${getAuthApiUrl()}/password/${email}`)
      req.setRequestHeader('Content-type', 'application/x-www-form-urlencoded')
      req.onload = function () {
        if (req.status === 204) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  validateHash: (email, hash) => {
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/password/${email}/${hash}`)
      req.setRequestHeader('Content-type', 'application/x-www-form-urlencoded')
      req.onload = function () {
        if (req.status === 204) {
          resolve(req.status)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  updatePassword: (email, hash, password) => {
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${getAuthApiUrl()}/password/${email}`)
      req.setRequestHeader('Content-type', 'application/x-www-form-urlencoded')
      req.onload = function () {
        if (req.status === 204) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(`hash=${hash}&password=${password}`)
    })
  },

  getBuildingSchedule: (start, end) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/training/${start}/${end}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          try {
            resolve(JSON.parse(req.response))
          } catch {
            reject(new Error(`Failed to load building schedule (status ${req.status})`))
          }
        } else {
          reject(new Error(`Failed to load building schedule (status ${req.status})`))
        }
      }
      req.onerror = () => {
        reject(new Error(`Failed to load building schedule (status ${req.status})`))
      }
      req.send()
    })
  },

  getBuildingPhoneNumberByBuildingId: async (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/communication/building/${buildingId}/phone`,
      {
        headers: { token: token },
      },
    )
    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(response.statusText)
    }
  },

  buyBuildingPhoneNumberByBuildingId: async (buildingId, body) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/communication/building/${buildingId}/phone`,
        body,
        {
          headers: { token: token },
        },
      )
      return response.data
    } catch (error) {
      throw new Error(error.response.data.message)
    }
  },

  cancelBuildingPhoneNumberByBuildingId: async (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/communication/building/${buildingId}/phone/cancel`,
        undefined,
        {
          headers: { token: token },
        },
      )
    } catch (error) {
      throw new Error(error.response.data.message)
    }
  },

  getTrainingsByBuildingId: (start, end, buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/training/${start}/${end}/${buildingId}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          try {
            resolve(JSON.parse(req.response))
          } catch {
            reject(new Error(`Failed to load building trainings (status ${req.status})`))
          }
        } else {
          reject(new Error(`Failed to load building trainings (status ${req.status})`))
        }
      }
      req.onerror = () => {
        reject(new Error(`Failed to load building trainings (status ${req.status})`))
      }
      req.send()
    })
  },

  getTrainingsForBuilding: (start, end, building) => {
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${import.meta.env.VITE_API_URL}/training-for-building/${start}/${end}/${building}`,
      )
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getUserClasses: (start, end, type = '') => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/training/${start}/${end}/${type}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getClientClasses: (start, end, type = '', clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${import.meta.env.VITE_API_URL}/training/clientHistory/${start}/${end}/${clientId}/${type}`,
      )
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getUserSchedule: (start, end) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/schedule/${start}/${end}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          const trainings = []
          res.forEach((obj) => {
            trainings.push(obj.trainingId)
          })
          resolve(trainings)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },
  signToTraining: (id, all) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/training/${id}${all ? '/all' : ''}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          resolve(req.response)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },
  signToPaidTraining: (id, data) => {
    const token = getCookie('token')
    if (!token) throw Error('user token missing')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/training/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          resolve(req.response)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(param(data))
    })
  },

  getSingleBuilding: (buildingId) => {
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/building/${buildingId}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getAllBuildings: () => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/building`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          try {
            resolve(JSON.parse(req.response))
          } catch {
            reject(new Error(`Failed to parse buildings response (status ${req.status})`))
          }
        } else {
          reject(new Error(`Failed to fetch buildings (status ${req.status})`))
        }
      }
      req.onerror = () => {
        reject(new Error(`Failed to fetch buildings (status ${req.status})`))
      }
      req.send()
    })
  },

  getNumberOfAllClientsInBuilding: (buildingId, trainerId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${import.meta.env.VITE_API_URL}/client/count/${buildingId}/${trainerId || ''}`,
      )
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getAllTrainers: () => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/trainer/all`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          const trainers = []
          res.forEach((obj) => {
            trainers.push(obj)
          })
          resolve(trainers)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  updateTrainerData: (userData, id) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/trainer/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(param(userData))
    })
  },

  getAllShiftManagers: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/building/${buildingId}/shiftManager`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  updateValetUser: (userData, valetId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/valet/${valetId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(param(userData))
    })
  },

  updateParkingStaffUser: (userData, parkingStaffId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/parking-staff/${parkingStaffId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(param(userData))
    })
  },

  deleteValetUser: (valetId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    const allowedStatuses = [200, 201, 204]
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/valet/${valetId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (allowedStatuses.includes(req.status)) {
          resolve(req.status)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  deleteParkingStaffUser: (parkingStaffId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    const allowedStatuses = [200, 201, 204]
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/parking-staff/${parkingStaffId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (allowedStatuses.includes(req.status)) {
          resolve(req.status)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  getAllValetUsers: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/building/${buildingId}/valet`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getAllParkingStaffUsers: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/parking-staff/find?buildingId=${buildingId}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getInactiveClients: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    const allowedStatuses = [200]
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/building/${buildingId}/client/inactive`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (allowedStatuses.includes(req.status) && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  createNewManagementUser: (userData, role) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    const allowedStatuses = [200, 201]
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/${role}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (allowedStatuses.includes(req.status)) {
          resolve(JSON.parse(req.response))
        } else {
          reject(req.response ? JSON.parse(req.response) : req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(param(userData))
    })
  },

  getAllPropertyManagers: () => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/propertyManager/all`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getAllPropertyManagersByBuildingId: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/building/${buildingId}/propertyManager`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getAllManagers: () => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/manager/0/999999999`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  createNewManager: (userData) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/manager`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(param(userData))
    })
  },

  updateManagerData: (userData, id) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/manager/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(param(userData))
    })
  },

  deleteManagementUserData: (managerId, role) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    const allowedStatuses = [200, 201, 204]
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/${role}/${managerId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (allowedStatuses.includes(req.status)) {
          resolve(req.status)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  updateManagementUserData: (userData, id = '', role) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    const allowedStatuses = [200, 201]
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/${role}/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (allowedStatuses.includes(req.status) && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(param(userData))
    })
  },

  getTrainingTypes: () => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/training-type`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getAllClients: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/client/0/1000000`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getGroupMembers: (start, pageSize, groupId, query) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${import.meta.env.VITE_API_URL}/group/${groupId}/members?skip=${start}&limit=${pageSize}${
          query !== '' ? '&query=' + query : ''
        }`,
      )
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const data = JSON.parse(req.response)
          resolve(data.members)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getClients: (start, pageSize, buildingId, trainerId, query) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${import.meta.env.VITE_API_URL}/client/${start}/${pageSize}/${buildingId}/${trainerId}${
          query !== '' ? '/' + encodeURIComponent(query) : ''
        }`,
      )
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const clients = JSON.parse(req.response)
          resolve(clients)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getClientTags: (clientId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/client/${clientId}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const { tags } = JSON.parse(req.response)
          resolve(tags)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getClientById: (clientId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/client/${clientId}`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  getBuildingTags: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/building/${buildingId}/client-tags`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const tags = JSON.parse(req.response)
          resolve(tags)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getClientsOrUnits: async (buildingId, skip, limit, search = '', yardiPropertyId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    const response = await axios.get(
      `${
        import.meta.env.VITE_API_URL
      }/client-or-unit?search=${search}&skip=${skip}&limit=${limit}&buildingId=${buildingId}${
        yardiPropertyId ? `&yardiPropertyId=${yardiPropertyId}` : ''
      }`,
      {
        headers: { token: token },
      },
    )
    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(response.statusText)
    }
  },

  getUnits: async (buildingId, skip, limit, search = '', propertyId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/unit`, {
      params: {
        buildingId,
        skip,
        limit,
        search,
        propertyId,
      },
      headers: {
        token: token,
      },
    })
  },

  getClientCreditCardData: async (clientId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/client/${clientId}/creditcard`,
      {
        headers: { token: token },
      },
    )
    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(response.statusText)
    }
  },

  setClientCreditCardData: (clientId, creditCardToken) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/creditcard`,
      {
        token: creditCardToken,
        clientId,
      },
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  setGuestCreditCardData: (guestId, creditCardToken) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/reservation/guest/${guestId}/creditcard`,
      {
        token: creditCardToken,
      },
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getClientsWithCount: (start, pageSize, buildingId, trainerId, query, filter = null) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${
          import.meta.env.VITE_API_URL
        }/client-with-count/${start}/${pageSize}/${buildingId}/${trainerId}${
          query !== '' ? '/' + encodeURIComponent(query) : ''
        }${filter ? `?filter=${filter}` : ''}`,
      )
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const clientsData = JSON.parse(req.response)
          resolve(clientsData)
        } else {
          reject(new Error(`Request failed with status code ${req.status}`))
        }
      }
      req.onerror = () => {
        reject(new Error(`Request failed with status code ${req.status}`))
      }
      req.send()
    })
  },

  getPackagesWithCount: (params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/packages/dashboard?${getPackagesWithCountQueryParams(
        params,
      )}`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  downloadPackagesCSV: (params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/packages/export-csv?${getPackagesCSVQueryParams(params)}`,
      {
        headers: {
          'content-type': 'text/plain;charset=utf-8',
          token: token,
        },
        responseType: 'blob',
      },
    )
  },

  addPackage: (packageNewData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/packages`, param(packageNewData), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  updatePackage: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/packages/${data.id}`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  updatePackageComment: (packageId, comment) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/packages/${packageId}`, param({ comment }), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  deletePackage: (packageId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/packages/${packageId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 || req.status === 201) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  getPackagesSettings: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/packages/settings?buildingId=${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  updatePackagesSettings: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/packages/settings`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  regeneratePackageRoomUrl: async (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.post(
      `${import.meta.env.VITE_API_URL}/package-room/generate-link`,
      { buildingId, packageRoomId },
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  verifyPackageRoomAccessCode: (code) => {
    const token = getTokenFromURL()
    if (!token) throw new Error('Not Authorized')

    return axios.post(
      `${import.meta.env.VITE_API_URL}/package-room/verify`,
      {
        code,
      },
      {
        headers: {
          token: token,
        },
      },
    )
  },

  verifyDeliveryAccessCode: (code) => {
    const token = getTokenFromURL()
    if (!token) throw new Error('Not Authorized')

    return axios.post(
      `${import.meta.env.VITE_API_URL}/carrier/verify-code`,
      {
        code,
      },
      {
        headers: {
          token: token,
        },
      },
    )
  },

  getPackageSignature: (packageId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/packages/${packageId}/signature`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
      responseType: 'blob',
    })
  },

  getNotifications: (page, type) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/notification/${page}/${type}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getBookingTypes: (page) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/booking-type/${page}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getBookingCategories: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/booking-category/all`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  createBookingCategory: (categoryData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/booking-category`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(JSON.parse(req.response))
        else reject(JSON.parse(req.response))
      }
      req.onerror = () => reject(req.status)
      req.send(param(categoryData))
    })
  },

  deleteBookingCategory: (categoryId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/booking-category/${categoryId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  editBookingCategory: (categoryData, categoryId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/booking-category/${categoryId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(param(categoryData))
    })
  },

  getMyBookings: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/reservation`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  createGuest: (guestData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/reservation/guest`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201 || req.status === 200) resolve(JSON.parse(req.response))
        else reject(JSON.parse(req.response))
      }
      req.onerror = () => reject(req.status)
      req.send(param(guestData))
    })
  },

  addBooking: (amenityId, newBookingData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/reservation`,
      { ...newBookingData, amenityId },
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getAmenities: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/amenity${id ? '/' + id : ''}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          try {
            resolve(JSON.parse(req.response))
          } catch {
            reject(new Error(`Failed to load amenities (status ${req.status})`))
          }
        } else {
          reject(new Error(`Failed to load amenities (status ${req.status})`))
        }
      }
      req.onerror = () => {
        reject(new Error(`Failed to load amenities (status ${req.status})`))
      }
      req.send()
    })
  },

  createNewAmenity: (amenityData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/amenity`, amenityData, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  editAmenity: (amenityData, amenityId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/amenity/${amenityId}`, amenityData, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  updateAmenityQuestions: (amenityId, questions) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/amenity/${amenityId}/questions`, questions, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  cancelAmenityReservations: (payload, amenityId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/amenity/${amenityId}/cancel`, payload, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  deleteAmenity: (amenityId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/amenity/${amenityId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(JSON.parse(req.response))
        else reject(JSON.parse(req.response))
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  getReservations: (buildingId, timestamp) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/reservation/${buildingId}/${timestamp}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  getMultidayReservations: (buildingId, timestamp) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${import.meta.env.VITE_API_URL}/reservation/multiday/${buildingId}/${timestamp}`,
      )
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  getAmenityReservationsInDateRange: (buildingId, from, to) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${import.meta.env.VITE_API_URL}/reservation/building/${buildingId}/${from}/${to}`,
      )
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  getMultiDayAmenityReservationsInDateRange: (buildingId, from, to) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${import.meta.env.VITE_API_URL}/reservation/multiday/${buildingId}/${from}/${to}`,
      )
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  cancelBooking: (bookingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/reservation/${bookingId}/cancel`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(JSON.parse(req.response))
        else reject(JSON.parse(req.response))
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  cancelReservation: (reservationId, refund, cancelNote, amount) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/reservation/${reservationId}/cancel/${refund ? '1' : '0'}`,
      {
        cancelNote,
        ...(amount !== undefined && { refundAmount: amount }),
      },
      {
        headers: {
          token: token,
        },
      },
    )
  },

  updateReservation: (reservationId, data, notifyEmail = false) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.patch(`${import.meta.env.VITE_API_URL}/reservation/${reservationId}`, data, {
      headers: { token: token },
      params: { notifyEmail },
    })
  },

  getRequests: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/reservation/pending/${buildingId}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  setRequestStatus: (requestData, status, refund) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/reservation/status/${status}/${refund ? 'true' : 'false'}`,
      param(requestData),
      {
        headers: {
          token: token,
        },
      },
    )
  },

  getGroupPosts: (groupId, skip, pageSize, scheduled) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/group/${groupId}/post`, {
      params: { skip, limit: pageSize, scheduled },
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getGroupsPosts: (buildingId, skip, pageSize, scheduled) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/group/building/${buildingId}/post`, {
      params: { skip, limit: pageSize, scheduled },
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getGroupSinglePost: (groupId, postId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/group/${groupId}/post/${postId}`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  getGroupById: (groupId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/group/${groupId}`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  getServicesQuestions: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/question`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  createServiceQuestion: (questionData) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/question`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(JSON.stringify(questionData))
    })
  },

  createServiceAnswer: (answerData, id) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/question/${id}/answer`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(JSON.stringify(answerData))
    })
  },

  updateServiceQuestion: (questionData, id) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/question/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(JSON.stringify(questionData))
    })
  },

  updateServiceAnswer: (answerData, answerId, questionId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/question/${questionId}/answer/${answerId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(JSON.stringify(answerData))
    })
  },

  deleteServiceQuestion: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/question/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  deleteServiceAnswer: (questionId, answerId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'DELETE',
        `${import.meta.env.VITE_API_URL}/question/${questionId}/answer/${answerId}`,
      )
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  getTimeScopes: async (amenityId, timestamp, locationId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    const { data: response } = await axios.get(
      `${import.meta.env.VITE_API_URL}/amenity/${amenityId}/availability/internal/${timestamp}`,
      {
        headers: { token: token },
        params: { locationId },
      },
    )
    return response
  },

  getAmenityReservations: async (amenityId, timestamp, locationId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    const { data: response } = await axios.get(
      `${import.meta.env.VITE_API_URL}/reservation/multiday/amenity/${amenityId}/${timestamp}`,
      {
        headers: { token: token },
        params: { locationId },
      },
    )
    return response
  },

  updateMaintenanceRequest: (maintenanceId, requestData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/v2/maintenance/${maintenanceId}`,
      param(requestData),
      {
        headers: {
          'content-type': 'application/json',
          'x-platform': 'web',
          token: token,
        },
      },
    )
  },

  getMaintenanceRequestActivities: (maintenanceId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/v2/maintenance/${maintenanceId}/activity`, {
      headers: {
        'content-type': 'application/json',
        'x-platform': 'web',
        token: token,
      },
    })
  },

  downloadMaintenanceRequestPDF: (maintenanceId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/v2/maintenance/${maintenanceId}/pdf`, {
      headers: {
        token: token,
      },
      responseType: 'blob',
    })
  },

  getRealPageConfig: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/real-page/config?buildingId=${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getEntrataConfig: (buildingId, entrataPropertyId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/entrata/config?buildingId=${buildingId}${
        entrataPropertyId ? `&entrataPropertyId=${entrataPropertyId}` : ''
      }`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getResmanConfig: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/v2/resman/config?buildingId=${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getYardiConfig: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/yardi/config?buildingId=${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getRentManagerConfig: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/rent-manager/config?buildingId=${buildingId}`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getAssigneesForFilter: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/building/${buildingId}/staff`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getYardiPropertyIds: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/yardi/property-id?buildingId=${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getNotes: (maintenanceId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/v2/maintenance/${maintenanceId}/note`, {
      headers: {
        'content-type': 'application/json',
        'x-platform': 'web',
        token: token,
      },
    })
  },

  getMaintenanceRequestDetailsForChat: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/v2/maintenance/${id}`, {
      headers: {
        'content-type': 'application/json',
        'x-platform': 'web',
        token: token,
      },
    })
  },

  getMaintenanceRequestsForClient: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/v2/maintenance/client/${id}`, {
      headers: {
        'content-type': 'application/json',
        'x-platform': 'web',
        token: token,
      },
    })
  },

  updateNote: (maintenanceId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/v2/maintenance/${maintenanceId}/note`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          'x-platform': 'web',
          token: token,
        },
      },
    )
  },

  handleFetchAnalyticsForMaintenanceRequests: (startDate, endDate, buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${
        import.meta.env.VITE_API_URL
      }/v2/maintenance/pm/analytics?startDate=${startDate}&endDate=${endDate}&buildingId=${buildingId}`,
      {
        headers: {
          'content-type': 'application/json',
          'x-platform': 'web',
          token: token,
        },
      },
    )
  },

  getMaintenanceStaffByBuildingId: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/building/${buildingId}/staff`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if ((req.status === 200 || req.status === 201) && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  createStaffPerson: (buildingId, data) => {
    const token = getCookie('token')
    const allowedStatuses = [200, 201]
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/staff`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (allowedStatuses.includes(req.status) && req.response) {
          resolve(JSON.parse(req.response))
        } else {
          reject(req.response ? JSON.parse(req.response) : req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(param(data, null, true))
    })
  },

  updateStaffPerson: (buildingId, staffId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/staff/${staffId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if ((req.status === 200 || req.status === 201) && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(JSON.parse(req.response))
        }
      }
      req.onerror = () => {
        reject(req.response ? JSON.parse(req.response) : req.status)
      }
      req.send(param(data, null, true))
    })
  },

  deleteStaffPerson: (buildingId, staffId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/building/${buildingId}/staff/${staffId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 204 || req.status === 200) resolve(req.status)
        else reject(JSON.parse(req.response))
      }
      req.onerror = () => reject(req.response ? JSON.parse(req.response) : req.status)
      req.send()
    })
  },

  getMaintenanceRequestsByBuildingId: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/v2/maintenance/filter`, data, {
      headers: {
        'content-type': 'application/json',
        'x-platform': 'web',
        token: token,
      },
    })
  },

  getMaintenanceRequestsAsAdminOrPM: (adminOrPMId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/maintenance/client/${adminOrPMId}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('x-platform', 'web')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if ((req.status === 200 || req.status === 201) && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getBookingClient: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/booking-type`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          if (req.response) {
            reject(JSON.parse(req.response))
          } else {
            reject(req.status)
          }
        }
      }
      req.onerror = () => {
        if (req.response) {
          reject(JSON.parse(req.response))
        } else {
          reject(req.status)
        }
      }
      req.send()
    })
  },

  getPropertyManagerData: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/propertyManager/${id}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          const clients = []
          res.forEach((obj) => {
            clients.push(obj)
          })
          resolve(clients)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getProspectClients: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/client/prospects`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          const clients = []
          res.forEach((obj) => {
            clients.push(obj)
          })
          resolve(clients)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  createNewEvent: (eventData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/training`, eventData, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  updateEvent: (id, eventData, type) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/training/${id}/${type}`, eventData, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  uploadEventImages: (formData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.post(`${import.meta.env.VITE_API_URL}/training/upload`, formData, {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  createNewEventByPropertyManager: (eventData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/propertyManager/training`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(param(eventData))
    })
  },

  createBookingType: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/booking-type`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(JSON.parse(req.response))
        else reject(JSON.parse(req.response))
      }
      req.onerror = () => reject(req.status)
      req.send(param(data))
    })
  },

  updateBookingType: (id, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/booking-type/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(param(data))
    })
  },

  getBookingType: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/booking-type/one/${id}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          if (req.response) {
            reject(JSON.parse(req.response))
          } else {
            reject(req.status)
          }
        }
      }
      req.onerror = () => {
        if (req.response) {
          reject(JSON.parse(req.response))
        } else {
          reject(req.status)
        }
      }
      req.send()
    })
  },

  updateChecklistTemplateStatus: (checklistId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/checklist-form/${checklistId}/active`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  updateChecklistTemplate: (checklistId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/checklist-form/${checklistId}`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  removeChecklistTemplate: (checklistId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/checklist-form/${checklistId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getUnitTypes: (buildingId, id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/unit-type?buildingId=${buildingId}${
        id ? `&checklistId=${id}` : ''
      }`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getManageInspectionAllTemplates: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/checklist-form?buildingId=${buildingId}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const allTemplates = JSON.parse(req.response)
          resolve(allTemplates)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  createNewChecklistTemplate: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/checklist-form`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  assignChecklist: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/checklist-form/assign`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  updateChecklist: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/checklist-assigned/${data.id}`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  removeChecklist: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/checklist-assigned/${id}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getChecklistSignature: (checklistId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/checklist-assigned/${checklistId}/signature`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
        responseType: 'blob',
      },
    )
  },

  getChecklistHistoryWithCount: (
    start,
    pageSize,
    buildingId,
    query,
    type,
    statuses,
    dateRange,
    residentsType,
    sortBy,
  ) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${import.meta.env.VITE_API_URL}/checklist-assigned?buildingId=${buildingId}${
          dateRange && dateRange[1]
            ? `&fromDate=${new Date(dateRange[0]).valueOf()}&toDate=${new Date(
                dateRange[1],
              ).valueOf()}`
            : ''
        }${type && type.value !== 'all' ? `&type=${type.value}` : ''}${
          residentsType && residentsType.value !== 'all' ? `&residents=${residentsType.value}` : ''
        }${
          statuses.some((status) => status.value === 'all')
            ? ''
            : `${statuses.map((status) => `&status=${status.value}`).join('&')}`
        }&skip=${start}&limit=${pageSize}&dueDateSort=${sortBy}${
          query !== '' ? '&search=' + query : ''
        }`,
      )
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const checklists = JSON.parse(req.response)
          resolve(checklists)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  downloadChecklistPDF: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/checklist-assigned/${id}/pdf`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
      responseType: 'blob',
    })
  },

  getCommunicationPortalHistoryWithCount: (
    start,
    pageSize,
    buildingId,
    query,
    types,
    status,
    dateRange,
  ) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    const params = {
      skip: start,
      limit: pageSize,
    }

    if (!types.some((type) => type.value === 'all')) {
      params.type = types.map((type) => type.value)
    }

    if (status && status.value !== 'all') {
      params.status = status.text
    }

    if (dateRange && dateRange[1]) {
      params.fromDate = startOfDay(new Date(dateRange[0])).valueOf()
      params.toDate = endOfDay(new Date(dateRange[1])).valueOf()
    }

    if (query !== '') {
      params.query = query
    }

    return axios.get(
      `${import.meta.env.VITE_API_URL}/communication/building/${buildingId}/history`,
      {
        headers: {
          token,
        },
        params,
      },
    )
  },

  getCommunicationMailResponseList: (start, query, messagesType, buildingId) => {
    const types = {
      notification: 'notification',
      email: 'mail',
    }

    const startPage = (start - 1) * 50
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    const baseUrl = `${import.meta.env.VITE_API_URL}/communication/building/${buildingId}/${
      types[messagesType]
    }/to`

    const url = query ? `${baseUrl}/${query}` : baseUrl

    return axios.get(url, {
      headers: {
        token,
      },
      params: {
        skip: startPage,
        limit: 50,
      },
    })
  },

  getAnnouncementRecipients: (
    messagesType,
    buildingId,
    recipientType,
    query = '',
    buildingNumber,
    floor,
    yardiPropertyId,
  ) => {
    const types = {
      notification: 'notification',
      email: 'mail',
    }

    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    const url = `${import.meta.env.VITE_API_URL}/communication/${types[messagesType]}/to`
    const params = {
      buildingId: `[${buildingId}]`,
      type: recipientType,
      buildingNumber,
      floor,
      yardiPropertyId,
      skip: 0,
      limit: 50,
      query: query,
    }

    return axios.post(url, null, {
      headers: {
        token: token,
        'Content-type': 'text/plain; charset=utf-8',
      },
      params,
    })
  },

  getBuildingCommunicationAnalytics: (buildingId, mailingList, from, to) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(
      `${import.meta.env.VITE_API_URL}/communication/building/${buildingId}/analytics`,
      {
        headers: {
          token,
        },
        params: {
          mailingList,
          fromDate: from,
          toDate: to,
        },
      },
    )
  },

  getSurveyDataDetails: (buildingId, selectedSurveyId, start, end) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${
        import.meta.env.VITE_API_URL
      }/communication/building/${buildingId}/survey/${selectedSurveyId}${
        start ? `?startDate=${start}&endDate=${end}` : ''
      }`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getSurveyTemplatesStatus: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/survey-templates/status?buildingId=${buildingId}`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  updateSurveyTemplatesStatus: (buildingId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/survey-templates/status?buildingId=${buildingId}`,
      data,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getSurveyHistory: (start, pageSize, buildingId, query, types, status, dateRange) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    const params = {
      buildingId,
      skip: start,
      limit: pageSize,
    }

    if (status && status.value !== 'all') {
      params.status = status.text
    }

    if (dateRange && dateRange[1]) {
      params.fromDate = new Date(dateRange[0]).valueOf()
      params.toDate = new Date(dateRange[1]).valueOf()
    }

    if (query !== '') {
      params.query = query
    }

    return axios.get(`${import.meta.env.VITE_API_URL}/survey/dashboard/`, {
      headers: {
        token,
      },
      params,
    })
  },

  getSurveyAnalytics: (buildingId, fromDate, toDate) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/v2/survey/dashboard/analytics`, {
      headers: {
        token,
      },
      params: {
        buildingId,
        fromDate,
        toDate,
      },
    })
  },

  deleteSurvey: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/survey/${id}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  copySurvey: (surveyId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/survey/${surveyId}/copy`, undefined, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  stopSurvey: (surveyId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/survey/${surveyId}`,
      param({ status: 'On hold' }),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token,
        },
      },
    )
  },

  resumeSurvey: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/survey/${id}`, param({ status: 'Ongoing' }), {
      headers: {
        'content-type': 'multipart/form-data',
        token,
      },
    })
  },

  getSurveyDetails: (selectedSurveyId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/v2/survey/${selectedSurveyId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getSurveyDetailsResponders: (surveyId, skip, limit, search = '', status = '') => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    const url = `${import.meta.env.VITE_API_URL}/survey/${surveyId}/responders`

    return axios.get(url, {
      headers: {
        token,
      },
      params: {
        skip,
        limit,
        query: search,
        status,
      },
    })
  },

  getSurveyDetailsAnalytics: (surveyId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/survey/${surveyId}/analytics`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getSurveyRecipients: (buildingIds, type, search = '') => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/survey/recipients`, {
      headers: {
        token,
      },
      params: {
        buildingId: `[${buildingIds}]`,
        type,
        limit: 100,
        query: search,
      },
    })
  },

  createSurvey: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/survey`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getSurveyResponsesClients: (
    buildingId,
    selectedSurveyId,
    responseNumber,
    start,
    end,
    _skip = 1,
    pageSize = 50,
    query,
  ) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    const params = {
      buildingId,
      skip: start,
      limit: pageSize,
    }

    if (status && status.value !== 'all') {
      params.status = status.text
    }

    if (dateRange && dateRange[1]) {
      params.fromDate = new Date(dateRange[0]).valueOf()
      params.toDate = new Date(dateRange[1]).valueOf()
    }

    if (query !== '') {
      params.query = query
    }

    return axios.get(`${import.meta.env.VITE_API_URL}/survey/dashboard/`, {
      headers: {
        token,
      },
      params,
    })
  },

  editSurvey: (data, surveyId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/survey/${surveyId}`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getNumberOfSelectedResponders: (data, buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/communication/building/${buildingId}/recipients/count`,
      data,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  sendEmail: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/communication/mail/send`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
        'x-platform': 'web',
      },
    })
  },

  sendScheduledEmail: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/communication/mail/schedule`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
        'x-platform': 'web',
      },
    })
  },

  updateScheduledEmail: (data, id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/communication/scheduled/mail/${id}`,
      param(data, '', true),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },

  removeScheduledEmail: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/communication/scheduled/mail/${id}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  sendSms: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios
      .post(`${import.meta.env.VITE_API_URL}/communication/sms/send`, param(data), {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
          'x-platform': 'web',
        },
      })
      .catch((error) => {
        if (error.response) {
          const {
            data: { message },
          } = error.response
          throw new Error(message)
        } else {
          throw new Error('Something went wrong.')
        }
      })
  },

  sendTestEmail: (data, buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/communication/building/${buildingId}/mail/test`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },

  getRecipientsByHistoryId: (id, buildingId, skip, limit = 50, search = '', status = '') => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    const url = `${
      import.meta.env.VITE_API_URL
    }/communication/building/${buildingId}/history/${id}/recipients`

    const params = {
      skip,
      limit,
      query: search,
      status,
    }

    return axios.get(url, {
      params,
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  sendPreviewEmail: (data, buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/communication/building/${buildingId}/mail/preview`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },

  sendNotification: (data, buildingId) => {
    const token = getCookie('token')
    const buildingTagsExists = data.buildingTags && data.buildingTags.length
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/communication${
        !buildingTagsExists ? `/building/${buildingId}` : ''
      }/notification/send`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
          'x-platform': 'web',
        },
      },
    )
  },

  sendScheduledNotification: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/communication/notification/schedule`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
          'x-platform': 'web',
        },
      },
    )
  },

  updateScheduledNotification: (data, id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/communication/scheduled/notification/${id}`,
      param(data, '', true),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },

  removeScheduledNotification: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(
      `${import.meta.env.VITE_API_URL}/communication/scheduled/notification/${id}`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getCommunicationDetails: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/communication/${id}/details`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getCommunicationAnalytics: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/communication/analytics/${id}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  bookService: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/book`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  createBooking: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/booking`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(param(data))
    })
  },

  updateMaintenance: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/maintainance/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  updateEventByPropertyManager: (id, eventData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/propertyManager/training/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(param(eventData))
    })
  },

  updateTrainerRepeat: (id, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/training/${id}/all`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(param(data))
    })
  },

  deleteEvent: (id, type, shouldRefund) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'DELETE',
        `${import.meta.env.VITE_API_URL}/training/${id}/${type}?refund=${shouldRefund}`,
      )
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 204) resolve(req.status)
        else reject(JSON.parse(req.response))
      }
      req.onerror = () => reject(req.response ? JSON.parse(req.response) : req.status)
      req.send()
    })
  },

  deleteBookingType: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/booking-type/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  deleteEventAll: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/training/${id}/true`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 204) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  getUserProducts: () => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/product`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getClientProducts: (id) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/product/client/${id}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  getAllProducts: () => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/shop`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  createNewProduct: (productData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/shop`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(JSON.parse(req.response))
        else reject(JSON.parse(req.response))
      }
      req.onerror = () => reject(req.status)
      req.send(param(productData))
    })
  },

  notifyParticipants: (id, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/training/notify/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(req.status)
        else {
          const res = JSON.parse(req.response)
          reject(res)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(param(data))
    })
  },

  createMaintenance: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/v2/maintenance`)
      req.setRequestHeader('x-platform', 'web')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(param(data))
    })
  },

  updateProductData: (productData, id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/shop/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(param(productData))
    })
  },

  deleteProduct: (id) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/shop/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 204) {
          resolve(req.status)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  deleteClient: (id) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/client/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) {
          resolve(req.status)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  declineClient: (id) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/client/${id}/decline`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) {
          resolve(req.status)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  activateClient: (id) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/client/${id}/activate`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  deleteUserFromEvent: (userId, eventId, role, withRefund) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'DELETE',
        `${import.meta.env.VITE_API_URL}/training/${
          role === 'Client' ? 'removeclient' : 'removeguest'
        }/${eventId}/${userId}?refund=${withRefund ? 'true' : 'false'}`,
      )
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 204) {
          resolve(req.status)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  createNewSKU: (SKUData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/sku`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(param(SKUData))
    })
  },

  updateSKUData: (SKUData, id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/sku/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(param(SKUData))
    })
  },

  deleteSKU: (id) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/sku/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 204) {
          resolve(req.status)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  updateBuildingData: async (buildingData, id) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios
      .put(`${import.meta.env.VITE_API_URL}/building/${id}`, param(buildingData), {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      })
      .then((res) => res.data)
      .catch((err) => Promise.reject(err.response?.data || err.response?.status || err))
  },

  resendActivationLink: (email) => {
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${getAuthApiUrl()}/activation/resend/${email}`)
      req.setRequestHeader('Content-type', 'application/x-www-form-urlencoded')
      req.onload = function () {
        if (req.status === 200) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  switchClientToProspect: (userId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/client/switch/${userId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 204) {
          resolve(req.status)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  createNewBuilding: (buildingData) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/building`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) {
          resolve(JSON.parse(req.response))
          return
        }
        try {
          reject(req.response ? JSON.parse(req.response) : req.status)
        } catch {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(param(buildingData))
    })
  },

  notify: (data) => {
    const token = getCookie('token')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/notify`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(req.response)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(param(data))
    })
  },

  getBuildingAnalytics: (start, end, role, buildings) => {
    const token = getCookie('token')
    const userRole = role === 'Administrator' ? 'adminanalytics' : 'pmanalytics'
    let params =
      buildings.length > 0
        ? '?' + buildings.map((building) => `buildingId=${building.id}`).join('&')
        : ''

    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/${userRole}/${start}/${end}${params}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },
  getBuildingAnalyticsChart: (start, end, type, scale, role, buildings) => {
    const token = getCookie('token')
    const userRole = role === 'Administrator' ? 'adminanalytics' : 'pmanalytics'
    let params =
      buildings.length > 0
        ? '?' + buildings.map((building) => `buildingId=${building.id}`).join('&')
        : ''

    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${
          import.meta.env.VITE_API_URL
        }/${userRole}/graph/${type}/${start}/${end}/${scale}${params}`,
      )
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  validateProductCodes: (skuId, giftCard, promoCode) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open(
        'GET',
        `${import.meta.env.VITE_API_URL}/shop/buy/valid/${skuId}/${giftCard}/${promoCode}`,
      )
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          const res = JSON.parse(req.response)
          reject(res)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  addOneProduct: (clientId, skuId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      let data = {
        clientId,
        skuId,
      }
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/product/add`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(JSON.parse(req.response))
        else reject(req.response)
      }
      req.onerror = () => reject(req.status)
      req.send(param(data))
    })
  },

  buyProduct: (skuId, cardToken, recipient, message, giftCard, promoCode) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      let data = {
        skuId: skuId,
        cardToken: cardToken,
      }
      if (recipient) data.recipient = recipient
      if (message) data.message = message
      if (giftCard) data.giftCard = giftCard
      if (promoCode) data.promoCode = promoCode
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/shop/buy`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(JSON.parse(req.response))
        else reject(req.response)
      }
      req.onerror = () => reject(req.status)
      req.send(param(data))
    })
  },

  getAllPromoCodes: () => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/promocode`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },
  createNewPromoCode: (data) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/promocode`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(JSON.parse(req.response))
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send(param(data))
    })
  },

  deletePromoCode: (hash) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/promocode/${hash}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 204) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  getPromoCode: (hash) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/promoCode/${hash}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  downloadClientsCsvList: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/client/exportcsv?buildingId=${buildingId}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const blob = new Blob([req.response], { type: 'text/csv' })
          resolve(blob)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  createNeighbourhoodPerks: (perksData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/Neighbourhood`, param(perksData), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  getNeighbourhoodPerks: (id) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/neighbourhood/building/${id}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  updateNeighbourhoodPerks: (perksData, id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/neighbourhood/${id}`, param(perksData), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  deleteNeighbourhoodPerks: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/neighbourhood/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 204) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },
  getBuildingGuests: async (buildingId, { skip, limit, search }) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/reservation/guest?buildingId=${buildingId}&search=${
        search || ''
      }&skip=${skip || 0}&limit=${limit || 100}`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(response.statusText)
    }
  },
  getClientGuests: (clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/client/${clientId}/guests`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },
  updateVisitorStatus: ({ id, type = 'guest', banned, suspended, bannedReason }) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/visitor/${id}/status`,
      { banned, suspended, bannedReason, type },
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  checkInClientGuest: (clientId, guestId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/client/${clientId}/guest/${guestId}/checkIn`,
      {},
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  checkInClientVendor: (vendorId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/vendor/${vendorId}/checkin`,
      {
        id: vendorId,
      },
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getTwilioToken: () => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/twilio-chat/token`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res.token)
        } else {
          reject(new Error(`Failed to fetch Twilio token (status: ${req.status})`))
        }
      }
      req.onerror = () => reject(new Error(`Failed to fetch Twilio token (status: ${req.status})`))
      req.send()
    })
  },

  getChatInboxes: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/chat-inbox?buildingId=${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getCustomChatInbox: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/chat-inbox/custom`, {
      params: { buildingId, all: true },
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getInvoices: (buildingId, skip = 0, limit = 4) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${
        import.meta.env.VITE_API_URL
      }/building/${buildingId}/sms-invoice?skip=${skip}&limit=${limit}`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  updateChatInboxes: (inboxId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getChatClients: (data) => {
    const uniqueIds = new Set(data)
    const uniqueIdsArray = Array.from(uniqueIds).filter(Number)
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/client/multipleids`,
      param(uniqueIdsArray, 'ids'),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },

  getChatAdditionalInfo: (conversationName) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/twilio-chat/${conversationName}/info`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  createSMSmessage: (messageData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/twilio-chat/send-sms`, messageData, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  createOrJoinConversation: (data) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/twilio-chat/create-or-join-conversation`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if ((req.status === 200 || req.status === 201) && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(JSON.stringify(data))
    })
  },
  // Admin and Managers array
  getHighManagementUsers: () => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/management`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },
  getMailingListForBuilding: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/mailinglist/building/${buildingId}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if ((req.status === 200 || req.status === 201) && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },
  getClientSubscribedMailingList: (clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/mailinglist/client/${clientId}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if ((req.status === 200 || req.status === 201) && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },
  setNewSubMailingListForClient: (clientId, data) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/mailinglist/client/${clientId}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if ((req.status === 200 || req.status === 201) && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => reject(req.status)
      req.send(JSON.stringify(data))
    })
  },

  getPropertyManagerNotificationsSettings: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/propertyManager/mails`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  updatePropertyManagerNotificationsSettings: (notificationsSettings) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/propertyManager/mails`,
      notificationsSettings,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getShiftManagerNotificationsSettings: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/shiftManager/mails`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  updateShiftManagerNotificationsSettings: (notificationsSettings) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/shiftManager/mails`, notificationsSettings, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getStaffNotificationsSettings: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/staff/mails`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  updateStaffNotificationsSettings: (notificationsSettings) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/staff/mails`, notificationsSettings, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getBuildingEssentials: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/building-essential/${buildingId}`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          resolve(res)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },

  createBuildingEssential: (buildingEssential) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('POST', `${import.meta.env.VITE_API_URL}/building-essential`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 201) resolve(JSON.parse(req.response))
        else reject(JSON.parse(req.response))
      }
      req.onerror = () => reject(req.status)
      req.send(param(buildingEssential))
    })
  },

  deleteBuildingEssential: (id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('DELETE', `${import.meta.env.VITE_API_URL}/building-essential/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(req.status)
        else reject(req.status)
      }
      req.onerror = () => reject(req.status)
      req.send()
    })
  },

  updateBuildingEssential: (buildingEssential) => {
    const { id, ...data } = buildingEssential
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/building-essential/${id}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200) resolve(JSON.parse(req.response))
        else reject(JSON.parse(req.response))
      }
      req.onerror = () => reject(req.status)
      req.send(param(data))
    })
  },

  getGroups: (buildingId, params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/group/building/${buildingId}`, {
      params,
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  createGroup: (buildingId, groupData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    try {
      return axios.post(
        `${import.meta.env.VITE_API_URL}/group/building/${buildingId}`,
        param(groupData),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            token: token,
          },
        },
      )
    } catch {
      throw new Error('An error occurred while creating the group')
    }
  },

  updateGroup: (newGroupData, groupId) => {
    const allowedStatuses = [200, 201]
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('PUT', `${import.meta.env.VITE_API_URL}/group/${groupId}`)
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (allowedStatuses.includes(req.status)) resolve(JSON.parse(req.response))
        else reject(JSON.parse(req.response))
      }
      req.onerror = () => reject(req.status)
      req.send(param(newGroupData))
    })
  },

  deleteGroup: (groupId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/group/${groupId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  banUserFromGroup: (groupId, userId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/group/${groupId}/user/${userId}/ban`,
      {},
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  unbanUserFromGroup: (groupId, userId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/group/${groupId}/user/${userId}/ban`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  deleteGroupsPost: (groupId, postId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/group/${groupId}/post/${postId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  approveGroupsPost: (groupId, postId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/group/${groupId}/post/${postId}/approve`,
      {},
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  dislikeGroupsPost: (groupId, postId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/group/${groupId}/post/${postId}/like`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  likeGroupsPost: (groupId, postId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/group/${groupId}/post/${postId}/like`,
      {},
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  createPostForMultipleGroups: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/group/post`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  createPost: (groupId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/group/${groupId}/post`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  updatePost: (groupId, postId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/group/${groupId}/post/${postId}`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },

  deletePostComment: (groupId, postId, commentId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(
      `${import.meta.env.VITE_API_URL}/group/${groupId}/post/${postId}/comment/${commentId}`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  addPostComment: (groupId, postId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/group/${groupId}/post/${postId}/comment`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },

  likeComment: (groupId, postId, commentId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/group/${groupId}/post/${postId}/comment/${commentId}/like`,
      {},
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  dislikeComment: (groupId, postId, commentId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(
      `${import.meta.env.VITE_API_URL}/group/${groupId}/post/${postId}/comment/${commentId}/like`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  fetchSocialLinks: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/sociallink?buildingId=${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getReviewContact: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/review/${buildingId}/contact`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  addNewSocialLink: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/sociallink`, param(data), {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  updateSocialLink: (data, socialLinkId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/sociallink/${socialLinkId}`, param(data), {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  removeSocialLink: (socialLinkId, buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(
      `${import.meta.env.VITE_API_URL}/sociallink/${socialLinkId}?buildingId=${buildingId}`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  fetchUnsplashPhotosByTerm: (searchTerm) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    if (searchTerm.length === 0 && !token) {
      return
    }

    return axios.get(
      `${import.meta.env.VITE_API_URL}/unsplash/search-pictures?query=${searchTerm}`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  reopenChat: (inboxId, buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/reopen`,
      { buildingId },
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  closeChat: (inboxId, buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/close`,
      { buildingId },
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  createInboxCustomReply: (inboxId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/custom-reply`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  updateInboxCustomReply: (inboxId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/custom-reply/${data.id}`,
      data,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  removeInboxCustomReply: (inboxId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(
      `${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/custom-reply/${data.id}`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  createChatNote: (conversationName, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/twilio-chat/${conversationName}/note`,
      data,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  cancelNumberSubscription: (inboxId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/phone/cancel`,
      undefined,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  updateNumberSubscription: (inboxId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/phone`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  cancelSmsSubscription: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/sms-package/cancel`,
      {},
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  updateSmsSubscription: (buildingId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/building/${buildingId}/sms-package`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  getSmsPackages: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/chat-inbox/prices`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  checkAreaCode: (areaCode) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/chat-inbox/phone?areaCode=${areaCode}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  getBuildingCreditCardData: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/building/${buildingId}/payment-info`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  getAllExternalGiftCards: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/reward/external/list`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
      params: buildingId ? { buildingId } : {},
    })
  },
  createExternalGiftCard: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/reward/external/send`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },
  createCustomGiftCard: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/reward/internal/send`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },
  resendGiftCardEmail: (rewardId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/reward/${rewardId}/resend`, null, {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },
  createCashRewardLink: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/reward/cash/send`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },
  fetchRewardsHistory: (params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/reward/history`, {
      params,
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  fetchRewardsAnalytics: (params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/reward/analytics`, {
      params,
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  fetchLoyaltyAnalytics: (buildingId, params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/loyalty/dashboard/analytics/building/${buildingId}`,
      {
        params,
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  getUnsplashImages: (query) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/unsplash/search-pictures?query=${query}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  getTrainersWithoutUserData: () => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()
      req.open('GET', `${import.meta.env.VITE_API_URL}/trainer/all-non-populated`)
      req.setRequestHeader('Content-type', 'text/plain; charset=utf-8')
      req.setRequestHeader('token', token)
      req.onload = () => {
        if (req.status === 200 && req.response) {
          const res = JSON.parse(req.response)
          const trainers = []
          res.forEach((obj) => {
            trainers.push(obj)
          })
          resolve(trainers)
        } else {
          reject(req.status)
        }
      }
      req.onerror = () => {
        reject(req.status)
      }
      req.send()
    })
  },
  addFundsToBuildingBalance: (buildingId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/reward/building/${buildingId}/topup`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },
  getChecklistInspectionDetails: (inspectionId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/checklist-unified/detail/${inspectionId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  getChecklistDashboardResidents: (buildingId, params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/checklist/dashboard/building/${buildingId}`, {
      params,
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  getResidentTasksDetails: (residentId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/checklist-assigned/client/${residentId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  createCustomTask: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/checklist-custom`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },
  getReservationDashboard: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    const params = new URLSearchParams()
    Object.keys(data).forEach((key) => {
      const value = data[key]
      if (value) {
        if (Array.isArray(value)) {
          value.forEach((element) => params.append(key, element))
        } else {
          params.append(key, value)
        }
      }
    })
    return axios.get(`${import.meta.env.VITE_API_URL}/reservation/dashboard`, {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
      params,
    })
  },
  getReservationDetails: (reservationId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/reservation/${reservationId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  updateCustomTask: (checklistId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/checklist-custom/${checklistId}`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },
  getShortCustomTasksList: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/checklist-custom/short`, {
      params: {
        buildingId,
      },
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  getCustomTasks: (params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/checklist-custom`, {
      params,
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  duplicateCustomTask: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/checklist-custom/duplicate`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },
  sendChecklistReminder: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/checklist/reminder`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  updateCustomTaskStatus: (taskId, status) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/checklist-custom/${taskId}/status`,
      param({ status }),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },
  sendUpsellingMessage: (data, buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/building/${buildingId}/upsell/email`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  getChecklistsSettings: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/checklist/settings/building/${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  updateChecklistsSettings: (buildingId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/checklist/settings/building/${buildingId}`,
      data,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  getAnalytics: (dateStart, dateEnd, buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/analytics-v2`, {
      params: { buildingId, dateStart, dateEnd },
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  fetchReservationsAnalytics: (params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/reservation/analytics`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
      params,
    })
  },
  getReservationNotes: (reservationId, isTimeBlock) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/${
        isTimeBlock ? 'time-block' : 'reservation'
      }/${reservationId}/notes`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  saveReservationNotes: (reservationId, data, isTimeBlock) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/${
        isTimeBlock ? 'time-block' : 'reservation'
      }/${reservationId}/notes`,
      data,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  deleteReservationNote: (reservationId, noteId, isTimeBlock) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(
      `${import.meta.env.VITE_API_URL}/${
        isTimeBlock ? 'time-block' : 'reservation'
      }/${reservationId}/notes/${noteId}`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  updateReservationNotes: (reservationId, noteId, data, isTimeBlock) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/${
        isTimeBlock ? 'time-block' : 'reservation'
      }/${reservationId}/notes/${noteId}`,
      data,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  deleteTimeBlock: (timeBlockId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/time-block/${timeBlockId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  getReservationsInDateRange: async (buildingId, amenityId, from, to) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    const {
      data: { results = [] },
    } = await axios.get(`${import.meta.env.VITE_API_URL}/reservation/dashboard`, {
      params: {
        buildingId,
        amenityId,
        status: 'approved',
        dateStart: from,
        dateEnd: to,
      },
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })

    return results
  },
  createTimeBlock: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/time-block`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  getAnalyticsReviews: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/analytics-v2/reviews?buildingId=${buildingId}`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  getAmenityReservationsInDateRangeByAmenityId: (amenityId, from, to) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/reservation/${amenityId}/${from}/${to}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  reservationsDownloadCSVReport: (params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/reservation/report`, {
      params,
      headers: {
        'content-type': 'text/plain;charset=utf-8',
        token: token,
      },
      responseType: 'blob',
    })
  },
  maintenanceDownloadCSVReport: (params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/v2/maintenance/csv`, params, {
      headers: {
        'content-type': 'text/plain;charset=utf-8',
        'x-platform': 'web',
        token: token,
      },
      responseType: 'blob',
    })
  },
  getBookingTypesByBuildingId: (buildingId, isActive) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/booking-type`, {
      params: { buildingId, ...(isActive !== undefined && { isActive }) },
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  getParkingTicket: (ticketNumber) => {
    return axios.get(`${import.meta.env.VITE_API_URL}/valet/ticket/${ticketNumber}`)
  },
  getValetSettings: (buildingId) => {
    return axios.get(`${import.meta.env.VITE_API_URL}/valet/settings?buildingId=${buildingId}`)
  },
  calculateParkingFee: (ticketNumber) => {
    return axios.get(`${import.meta.env.VITE_API_URL}/valet/ticket/${ticketNumber}/calculate-fee`)
  },
  requestVehicle: (serviceFee, ticketNumber) => {
    return axios.post(`${import.meta.env.VITE_API_URL}/valet/ticket/${ticketNumber}/request`, {
      serviceFee,
    })
  },
  valetDownloadCSVReport: (params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/valet/dashboard/export-csv`, {
      params,
      headers: {
        'content-type': 'text/plain;charset=utf-8',
        token: token,
      },
      responseType: 'blob',
    })
  },
  prepayWithResidentBalance: (ticketNumber) => {
    return axios.post(`${import.meta.env.VITE_API_URL}/parking/${ticketNumber}/payment`)
  },
  payForParking: (serviceFee, ticketNumber, tip = 0) => {
    return axios.post(`${import.meta.env.VITE_API_URL}/valet/ticket/${ticketNumber}/payment`, {
      serviceFee,
      tip,
    })
  },
  getParkingDataDashboard: (params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/valet/dashboard`, {
      params,
      headers: {
        'content-type': 'text/plain;charset=utf-8',
        token,
      },
    })
  },
  getParkingAnalytics: (params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/valet/analytics`, {
      params,
      headers: {
        'content-type': 'text/plain;charset=utf-8',
        token,
      },
    })
  },
  updateValetSettings: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/valet/settings`, data, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },
  checkoutVehicle: (vehicleId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/vehicle/${vehicleId}/check-out`,
      {},
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },
  fetchLoyaltyLeaderboard: (buildingId, params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/loyalty/dashboard/leaders/building/${buildingId}`,
      {
        params,
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  fetchClientRewards: (clientId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/loyalty/dashboard/client/${clientId}/reward-history`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  fetchLoyaltyProfile: (clientId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/loyalty/dashboard/client/${clientId}/profile`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  createLoyaltyActivity: (props) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/loyalty${props.clientId ? '/client' : ''}/activity`,
      props,
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },
  updateLoyaltySetting: (buildingId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/loyalty/building/${buildingId}/settings`,
      data,
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },
  fetchLoyaltySettings: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/loyalty/settings/building/${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },
  fetchLoyaltyBuildingActivities: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/loyalty/building/${buildingId}/activities`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },
  deleteLoyaltyClientActivity: (activityId, clientId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/loyalty/client/activity`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
      data: {
        id: activityId,
        clientId,
      },
    })
  },
  completeLoyaltyClientActivity: (activityId, clientId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/loyalty/client/activity/complete`,
      {
        id: activityId,
        clientId,
      },
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },
  loyaltyApproveActivity: (activityId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/loyalty/activity/${activityId}/approve`,
      {},
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },
  updateLoyaltyBuildingActivityStatus: (activityId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/loyalty/activity/${activityId}/status`,
      data,
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },
  deleteLoyaltyBuildingActivity: (activityId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/loyalty/activity/${activityId}`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },
  updateLoyaltyBuildingActivity: (activityId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/loyalty/activity/${activityId}`, data, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },
  createLoyaltyReward: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/loyalty/reward`, data, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },
  loyaltyDeclineActivity: (activityId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/loyalty/activity/${activityId}/decline`,
      {},
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },
  fetchLoyaltyDashboard: (buildingId, params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/loyalty/dashboard/building/${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
      params,
    })
  },
  loyaltyActivityCancelPoints: (activityId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/loyalty/activity/${activityId}/cancel-points`,
      null,
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },
  fetchLoyaltyBuildingRewards: (buildingId, params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/loyalty/building/${buildingId}/rewards`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
      params,
    })
  },
  updateLoyaltyBuildingRewardStatus: (rewardId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/loyalty/reward/${rewardId}/status`, data, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },
  getUtilityProviders: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/checklist/utility-providers`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },
  fetchCustomTaskById: (taskId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/checklist-custom/${taskId}`, {
      headers: {
        'content-type': 'multipart/form-data',
        token,
      },
    })
  },
  createUtilityProvider: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/checklist/utility-providers`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token,
      },
    })
  },
  deleteUtilityProvider: (providerId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.delete(
      `${import.meta.env.VITE_API_URL}/checklist/utility-providers/${providerId}`,
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },
  updateUtilityProvider: (providerId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/checklist/utility-providers/${providerId}`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token,
        },
      },
    )
  },
  fetchResidentStatuses: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/pms-status`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },
  getLoyaltyAmenities: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/loyalty/building/${buildingId}/available-amenities`,
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },
  getYardiPropertyIdsByBuildingId: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/yardi/property-id?buildingId=${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },
  getClientFieldsByBuildingId: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/client/fields?buildingId=${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },
  getYardiCustomRecordsByBuildingId: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/yardi/building/${buildingId}/custom-records`,
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },
  updateTaskAssignDays: (id, days) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/checklist-custom/${id}/assign-days`,
      { days },
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },
  updateTaskDueDateDays: (id, days) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/checklist-custom/${id}/due-days`,
      { days },
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },

  addNewVisitorGuest: async (clientId, guestData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.post(`${import.meta.env.VITE_API_URL}/client/${clientId}/guest`, guestData, {
      headers: {
        token: token,
        'content-type': 'multipart/form-data',
      },
    })
  },

  updateVisitorGuest: async (id, guestData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.put(`${import.meta.env.VITE_API_URL}/client/guest/${id}`, guestData, {
      headers: {
        token: token,
        'content-type': 'multipart/form-data',
      },
    })
  },

  addNewVisitorVendor: async (clientId, vendorData) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.post(`${import.meta.env.VITE_API_URL}/vendor/create-by-employee`, vendorData, {
      headers: {
        token: token,
      },
    })
  },

  getYardiTransactions: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/client/transactions`, {
      headers: {
        token: token,
      },
    })
  },

  getPaymentHistory: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/payment/history`, {
      headers: {
        token: token,
      },
    })
  },

  // ponytail: kept in the legacy api.js layer to match the connected .jsx Payments screen these
  // calls serve; a modern React Query slice for 4 calls into a Redux-connected screen is churn.
  getUserPaymentSettings: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/client/payment-settings`, {
      headers: {
        token: token,
      },
    })
  },

  getAutopaySettings: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/payment/autopay`, {
      headers: {
        token: token,
      },
    })
  },

  setClientAutopay: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/payment/autopay`, data, {
      headers: {
        token: token,
      },
    })
  },

  // Shared by autopay and one-off Pay Now — both save a card/bank account to the resident's Stripe
  // customer the same way. Returns { clientSecret, setupIntentId, customerId }.
  createSetupIntent: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/payment/setup-intent`, undefined, {
      headers: {
        token: token,
      },
    })
  },

  getPaymentMethods: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/payment/methods`, {
      headers: {
        token: token,
      },
    })
  },

  deletePaymentMethod: (paymentMethodId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/payment/methods/${paymentMethodId}`, {
      headers: {
        token: token,
      },
    })
  },

  getSalesforceCompanies: (clientId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/salesforce/client/${clientId}/companies`, {
      headers: {
        token: token,
      },
    })
  },

  getSalesforceTransactions: (clientId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/salesforce/client/${clientId}/ledger`, {
      headers: {
        token: token,
      },
    })
  },

  getYardiProcessingFee: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/building/${buildingId}/processing-fee`, {
      headers: {
        token: token,
      },
    })
  },

  addSalesforceBillingsSummary: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/salesforce/client/${data.clientId}/billings-summary`,
      data.body,
      {
        headers: {
          token: token,
        },
      },
    )
  },

  // Spec: [Tech. Spec] Rent Payments via Stripe Connect v1, §4.2. Creates and confirms the Stripe
  // PaymentIntent server-side (Separate Charges & Transfers + on_behalf_of) using paymentMethodId.
  // `data` is
  // { billingAmount, totalAmount, paymentMethodId, paymentType: 'CreditCard' | 'ACH', chargeCodes: [{ chargeCode, amount }] },
  // all cents.
  createRentPayment: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/client/transactions`, data, {
      headers: {
        token: token,
      },
    })
  },
  proceedPaymentYardiBatch: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/yardi/client/transactions-batch`, data, {
      headers: {
        token: token,
      },
    })
  },

  syncCalendar: (type) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/${type}/calendar/auth-url`, {
      headers: {
        token: token,
      },
    })
  },

  getVoicemailMessages: (inboxId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/voicemail-message`, {
      headers: {
        token: token,
      },
    })
  },

  createVoicemailMessage: (inboxId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/voicemail-message`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },

  deleteVoicemailMessage: (inboxId, messageId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(
      `${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/voicemail-message/${messageId}`,
      {
        headers: {
          token: token,
        },
      },
    )
  },

  updateVoicemailMessage: (inboxId, messageId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.patch(
      `${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/voicemail-message/${messageId}`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },

  getCallSchedule: (inboxId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/call-schedule`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },
  updateCallSchedule: (inboxId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/call-schedule`, data, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  getEscalationSettings: (inboxId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/escalation-settings`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  updateEscalationSettings: (inboxId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/chat-inbox/${inboxId}/escalation-settings`,
      data,
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },

  notifyScheduledInspections: (inspectionIds) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/inspection/scheduled/notify`,
      inspectionIds,
      {
        headers: {
          token: token,
        },
      },
    )
  },

  updateInspectionSetup: (setupId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/inspection/setup/${setupId}`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getInspectionTemplates: (buildingId, params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/inspection/template`, {
      params: {
        buildingId,
        ...params,
      },
      headers: {
        token: token,
      },
    })
  },

  createInspectionFromTemplate: (params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/inspection/setup/from-template`, params, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  createInspectionTemplate: (params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/inspection/template`, param(params), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  updateInspectionTemplate: (templateId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/inspection/template/${templateId}`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },

  duplicateInspectionTemplates: (templateIds) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/inspection/template/duplicate`,
      templateIds,
      {
        headers: {
          token: token,
        },
      },
    )
  },

  getInspectionById: (inspectionId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/inspection/${inspectionId}`, {
      headers: {
        token: token,
      },
    })
  },

  getScheduledInspections: (buildingId, params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/inspection/scheduled`, {
      params: {
        buildingId,
        ...params,
      },
      headers: {
        token: token,
      },
    })
  },

  getInspectionSetups: (buildingId, params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/inspection/setup`, {
      params: {
        buildingId,
        ...params,
      },
      headers: {
        token: token,
      },
    })
  },

  updateScheduledInspection: (inspectionId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/inspection/scheduled/${inspectionId}`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  inspectionsBulkDelete: (deleteIds) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/inspection/delete-many`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
      data: {
        ids: deleteIds,
      },
    })
  },

  createInspectionSetup: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/inspection/setup`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  getPropertyUnitTypes: (buildingId, propertyId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/unit/type`, {
      params: {
        buildingId,
        propertyId,
      },
      headers: {
        token: token,
      },
    })
  },

  // Expected payload: { clientId, commonAreaIds?, extraRooms?, expirationDate? }
  // The resident's email, buildingId, and unit are looked up server-side from the Client record.
  createKeylessAccessDormakabaKey: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/dormakaba/client`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  testDormakabaIntegration: (credentials) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/dormakaba/connection-test`, credentials, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  notifyAssignee: (data, id) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/v2/maintenance/${id}/notify`, data, {
      headers: {
        'content-type': 'multipart/form-data',
        'x-platform': 'web',
        token: token,
      },
    })
  },

  getFullTranscription: (url) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(url, {
      headers: {
        token: token,
      },
    })
  },

  deleteChatMessage: (chatMessageSid) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(
      `${import.meta.env.VITE_API_URL}/twilio-chat/escalation/${chatMessageSid}`,
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
        data: {
          chatMessageSid,
        },
      },
    )
  },

  addLuxerToBuilding: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/luxer`, data, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  fetchPMCompanyList: (name, limit = 1000) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/${name}`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
      params: {
        limit,
      },
    })
  },

  deleteLuxerFromBuilding: (luxerId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/luxer/${luxerId}`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  createPMCompany: (name, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/${name}`, data, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  fetchPaymentSettings: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/building/${buildingId}/payment-settings`, {
      headers: { token: token },
    })
  },

  updatePaymentSettings: (buildingId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/payment-settings`,
      data,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  fetchYardiProperties: (managementId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/v2/yardi/properties`, {
      params: {
        yardiManagementId: managementId,
      },
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  getYardiProperty: (managementId, propertyId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/v2/yardi/property`, {
      params: {
        yardiManagementId: managementId,
        yardiPropertyId: propertyId,
      },
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  fetchEntrataProperties: (managementId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/entrata/properties`, {
      params: {
        entrataManagementId: managementId,
      },
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  getEntrataProperty: (managementId, propertyId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/v2/entrata/property`, {
      params: {
        entrataManagementId: managementId,
        entrataPropertyId: propertyId,
      },
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  fetchRentManagerProperties: (managementId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/rent-manager/properties`, {
      params: {
        rentManagerManagementId: managementId,
      },
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  getRentManagerProperty: (managementId, propertyId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/rent-manager/property`, {
      params: { rentManagerManagementId: managementId, rentManagerPropertyId: propertyId },
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  updateBuildingIntegration: (data, buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/integration`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token,
        },
      },
    )
  },

  disconnectBuildingIntegration: (buildingId, pms) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/integration/disconnect`,
      { pms },
      {
        headers: {
          'content-type': 'application/json',
          token,
        },
      },
    )
  },

  updateBuildingIntegrationBrivo: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/brivo/create`, data, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  updateBuildingIntegrationSwiftlane: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/swiftlane`, data, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  updateBuildingIntegrationSaltoSpace: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/salto-space/integration`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getBuildingSaltoSpaceAccessPoints: (buildingId) => {
    const token = getCookie('token')
    return axios.get(`${import.meta.env.VITE_API_URL}/salto-space/access-points/${buildingId}`, {
      headers: {
        token,
      },
    })
  },

  updateSaltoSpaceUser: (
    doorIds,
    permissionGroupIds,
    accessLevelIds,
    expirationDate,
    email,
    authorizationCodes,
  ) => {
    const token = getCookie('token')
    return axios.patch(
      `${import.meta.env.VITE_API_URL}/salto-space/user`,
      {
        email,
        expirationDate,
        doorIds,
        permissionGroupIds,
        accessLevelIds,
        authorizationCodes,
      },
      {
        headers: {
          token,
        },
      },
    )
  },

  getSaltoSpaceUser: (email) => {
    const token = getCookie('token')
    return axios.get(`${import.meta.env.VITE_API_URL}/salto-space/user`, {
      params: {
        email,
      },
      headers: {
        token,
      },
    })
  },

  isSaltoSpaceUserConnected: (email) => {
    const token = getCookie('token')
    return axios.get(`${import.meta.env.VITE_API_URL}/salto-space/isconnected`, {
      params: {
        email,
      },
      headers: {
        token,
      },
    })
  },

  testSaltoSpaceIntegration: ({ url, apiKey }) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/salto-space/test`,
      { url, apiKey },
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  syncSaltoSpaceAccessPoints: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/salto-space/access-points/${buildingId}/sync`,
      {},
      {
        headers: {
          token,
        },
      },
    )
  },

  tipsPayment: (totalAmount, staffName) => {
    return axios.post(`${import.meta.env.VITE_API_URL}/tips/payment`, {
      tip: totalAmount,
      staffName,
    })
  },

  uploadImage: (type, imageFile) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    const formData = new FormData()
    formData.append('type', type)
    formData.append('image', imageFile)

    return axios.post(`${import.meta.env.VITE_API_URL}/image`, formData, {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  getGuestPassesByClientId: (clientId, params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/guest-pass/${clientId}/profile`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
      params,
    })
  },

  getGuestPassDetailsByClientId: (shuffledClientId, params) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/guest-pass/${shuffledClientId}/details`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
      params,
    })
  },

  addGuestPassesByClientId: (clientId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/guest-pass/${clientId}/checkin`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  updateGuestPassesByClientId: (clientId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/guest-pass/${clientId}`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getGuestPassVisitsLogs: (visitId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/guest-pass/visit/${visitId}/logs`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  updateGuestPassVisit: (visitId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(`${import.meta.env.VITE_API_URL}/guest-pass/visit/${visitId}`, data, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  findPackageRoomResidents: () => {
    const token = getTokenFromURL()
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/package-room/residents`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getPackageRoomSettings: () => {
    const token = getTokenFromURL()
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/package-room/settings`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  uploadPackageRoomImage: (imageFile) => {
    const token = getTokenFromURL()
    if (!token) throw new Error('Not Authorized')

    const formData = new FormData()
    formData.append('image', imageFile)

    return axios.post(`${import.meta.env.VITE_API_URL}/package-room/upload`, formData, {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  getPackageRoomWeeklyCode: () => {
    const token = getTokenFromURL()
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/package-room/weekly`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getPackageLog: ({ buildingId, skip, limit, startDate, endDate }) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/package-room/sessions`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
      params: {
        buildingId,
        skip,
        limit,
        startDate,
        endDate,
      },
    })
  },

  getInspectionAnalytics: (buildingId, { startDate, endDate }) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/inspection/analytics`, {
      params: {
        buildingId,
        startDate,
        endDate,
      },
      headers: {
        token: token,
      },
    })
  },

  getGuestDetails: (guestId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/client-guest/${guestId}`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  getVendorDetails: (vendorId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/vendor/${vendorId}`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  getVisitors: async ({ buildingId, type, search, skip, limit, start, end }) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/visitor`, {
      headers: { token: token },
      params: { search, skip, limit, buildingId, type, start, end },
    })
  },

  getVisitorAnalytics: (buildingId, period) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/visitor-stats/${buildingId}/${period}`, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  getInspectionPdfReport: (inspectionId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/inspection/${inspectionId}/pdf`, {
      headers: {
        token: token,
      },
      responseType: 'blob',
    })
  },

  fetchPaymentSettings: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/building/${buildingId}/payment-settings`, {
      headers: { token: token },
    })
  },

  updatePaymentSettings: (buildingId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/payment-settings`,
      data,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  syncPackageSession: ({ packages, startTime, endTime }) => {
    const token = getTokenFromURL()
    if (!token) throw new Error('Not Authorized')

    return axios.post(
      `${import.meta.env.VITE_API_URL}/package-room/packages`,
      { packages, startTime, endTime },
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  syncOfflinePackageSession: ({ packages, startTime, endTime }) => {
    const token = getTokenFromURL()
    if (!token) throw new Error('Not Authorized')

    return axios.post(
      `${import.meta.env.VITE_API_URL}/package-room/packages`,
      { packages, startTime, endTime, offline: true },
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getPackageRoomSessionDetails: (sessionId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/package-room/session/${sessionId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getImprovedWithAIWriter: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/ai/writer`, data, {
      headers: {
        'content-type': 'application/json',
        token,
      },
    })
  },

  getEventStatistics: ({ eventId }) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/training/${eventId}/attendee/stats`, {
      headers: { token: token },
    })
  },

  getEventAttendees: ({ eventId, search, filter }) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/training/${eventId}/attendees`, {
      headers: { token: token },
      params: { search, filter },
    })
  },

  addEventMember: (eventId, clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')

    return axios.post(
      `${import.meta.env.VITE_API_URL}/training/${eventId}/member`,
      { clientId },
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getParkingSpots: ({ buildingId, search, skip, limit }) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/parking`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
      params: { search, skip, limit, buildingId },
    })
  },

  createSaltoSpaceUser: (email, buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/salto-space/user`,
      { email, buildingId },
      { headers: { token } },
    )
  },

  connectToSaltoSpace: (email, buildingId, saltoUserId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/salto-space/connect`,
      { email, buildingId, saltoUserId },
      { headers: { token } },
    )
  },

  getDisconnectedSaltoSpaceUsers: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/salto-space/disconnected/${buildingId}`, {
      headers: { token },
    })
  },
  getSaltoSpaceFobEncoders: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/salto-space/fob-encoders/${buildingId}`, {
      headers: { token },
    })
  },
  issueSaltoSpaceFob: (buildingId, encoderId, userEmail, fobAccess, force = false) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/salto-space/issue-fob`,
      { buildingId, encoderId, userEmail, ...fobAccess, force },
      { headers: { token } },
    )
  },
  updateSaltoSpaceFob: (buildingId, userEmail, encoderId, fobAccess) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/salto-space/update-fob`,
      { buildingId, userEmail, encoderId, ...fobAccess },
      { headers: { token } },
    )
  },
  revokeSaltoSpaceFob: (buildingId, userEmail, encoderId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/salto-space/revoke-fob`,
      { buildingId, userEmail, encoderId },
      { headers: { token } },
    )
  },

  disconnectSaltoSpaceUser: (email) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/salto-space/disconnect`, {
      params: { email },
      headers: { token },
    })
  },

  createVisionLineUser: (clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/seam-visionline/users`,
      { clientId },
      { headers: { token } },
    )
  },

  createSwiftlaneUser: (userEmail, buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')

    return axios.post(
      `${import.meta.env.VITE_API_URL}/swiftlane/resident/${buildingId}`,
      { userEmail },
      { headers: { token } },
    )
  },

  getVisionLineEntrances: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/seam-visionline/entrances`,
      { headers: { token } },
    )
  },

  getSwiftlaneUsersMatching: (userEmail, buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.post(
      `${import.meta.env.VITE_API_URL}/swiftlane/users/matching/${buildingId}`,
      { userEmail },
      {
        headers: {
          token,
        },
      },
    )
  },

  mapSwiftlaneUserToElos: (swiftlaneUserId, userId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.post(
      `${import.meta.env.VITE_API_URL}/swiftlane/users/map`,
      { swiftlaneUserId, userId },
      {
        headers: {
          token,
        },
      },
    )
  },

  getSwiftlaneUserGroupsByBuildingId: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/swiftlane/user-group/${buildingId}`, {
      headers: { token },
    })
  },

  updateVisionLineUser: (clientId, entranceIds) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/seam-visionline/users/${clientId}`,
      { entranceIds },
      { headers: { token } },
    )
  },

  isVisionLineUserConnected: (clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/seam-visionline/users/${clientId}/is-connected`,
      {
        headers: { token },
      },
    )
  },

  getDisconnectedVisionLineUsers: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/seam-visionline/disconnected-users/${buildingId}`,
      {
        headers: { token },
      },
    )
  },

  connectVisionLineUser: (clientId, seamVisionlineId = null) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    const body = seamVisionlineId ? { seamVisionlineId } : {}
    return axios.post(
      `${import.meta.env.VITE_API_URL}/seam-visionline/users/${clientId}/connect`,
      body,
      { headers: { token } },
    )
  },

  updateSwiftlaneUserGroups: (buildingId, userEmail, userGroupIds, expirationDate) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')

    return axios.put(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/swiftlane/resident`,
      { userEmail, userGroupIds, expirationDate },
      { headers: { token } },
    )
  },

  getSwiftlaneUserGroupsByUserId: (buildingId, userId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')

    return axios.get(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/swiftlane/resident/${userId}`,
      { headers: { token } },
    )
  },

  createVisionLineUser: (clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/seam-visionline/users`,
      { clientId },
      { headers: { token } },
    )
  },

  getVisionLineUser: (clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/seam-visionline/users/${clientId}`, {
      headers: { token },
    })
  },

  updateVisionLineUser: (clientId, entranceIds) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/seam-visionline/users/${clientId}`,
      { entranceIds },
      { headers: { token } },
    )
  },

  isVisionLineUserConnected: (clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/seam-visionline/users/${clientId}/is-connected`,
      {
        headers: { token },
      },
    )
  },

  getDisconnectedVisionLineUsers: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/seam-visionline/disconnected-users/${buildingId}`,
      {
        headers: { token },
      },
    )
  },

  connectVisionLineUser: (clientId, seamVisionlineId = null) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    const body = seamVisionlineId ? { seamVisionlineId } : {}
    return axios.post(
      `${import.meta.env.VITE_API_URL}/seam-visionline/users/${clientId}/connect`,
      body,
      { headers: { token } },
    )
  },

  revokeVisionLineEndpoint: (clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.delete(
      `${import.meta.env.VITE_API_URL}/seam-visionline/users/${clientId}/endpoint`,
      { headers: { token } },
    )
  },

  getProviderBusinessAuthUrl: (buildingId, provider) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/${provider}/business/auth-url`, {
      headers: { token },
      params: { buildingId },
    })
  },

  getProviderLocationsList: (buildingId, provider) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/reputation/location`, {
      headers: { token },
      params: { buildingId, provider },
    })
  },

  updateProviderLocation: (buildingId, provider, locationId, locationName) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/reputation/location`,
      {
        buildingId,
        provider,
        locationId,
        locationName,
      },
      { headers: { token } },
    )
  },
  getBuildingProviders: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/reputation/provider`, {
      headers: { token },
      params: { buildingId },
    })
  },

  disconnectProvider: (buildingId, provider) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/reputation/provider/disconnect`,
      {
        buildingId,
        provider,
      },
      {
        headers: { token },
      },
    )
  },

  connectApartmentsProvider: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/apartments/connect`,
      {
        buildingId,
      },
      {
        headers: { token },
      },
    )
  },

  saveApartmentsLocation: (buildingId, locationId, locationName) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/reputation/location`,
      {
        buildingId,
        locationId,
        locationName,
        provider: 'apartments',
      },
      {
        headers: { token },
      },
    )
  },

  updateBuildingApartmentsId: (buildingId, apartmentsId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/apartments/${buildingId}`,
      { locationId: apartmentsId },
      {
        headers: { token },
      },
    )
  },

  getReputationAnalytics: (buildingId, from, to) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/reputation/analytics`, {
      headers: { token },
      params: { buildingId, from, to },
    })
  },

  getReputationReviews: (buildingId, filter) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/reputation/review`, {
      headers: { token },
      params: { buildingId, ...filter },
    })
  },

  getReputationReviewNotRepliedCount: (buildingId, filter) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/reputation/review/noreply/count`, {
      headers: { token },
      params: { buildingId, ...filter },
    })
  },

  postReputationReviewReply: (reviewId, reply) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/reputation/review/reply`,
      { reply, reviewId },
      { headers: { token } },
    )
  },

  deleteReputationReviewReply: (reviewId, replyId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.delete(
      `${import.meta.env.VITE_API_URL}/reputation/review/${reviewId}/reply/${replyId}`,
      {
        headers: { token },
      },
    )
  },

  getRemoteLockDevices: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/remote-lock/locks`, {
      headers: { token },
      params: { buildingId },
    })
  },

  getRemoteLockUserDevices: (clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/remote-lock/users/${clientId}`, {
      headers: { token },
    })
  },

  createRemoteLockUser: (clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/remote-lock/users`,
      { clientId },
      { headers: { token } },
    )
  },

  reassignDoorsRemoteLockUser: (clientId, lockIds, expiryDate) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/remote-lock/users/${clientId}`,
      { userId: clientId, lockIds, ...(expiryDate ? { expiryDate } : {}) },
      { headers: { token } },
    )
  },

  getRemoteLockDisconnectedUsers: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/remote-lock/disconnected?buildingId=${buildingId}`,
      {
        headers: { token },
      },
    )
  },

  connectRemoteLockUser: (data) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/remote-lock/connect`, data, {
      headers: { token },
    })
  },
  connectRemoteLockStaff: (buildingId, data) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/remote-lock/connect-staff`,
      data,
      { headers: { token } },
    )
  },
  issueRemoteLockFob: (buildingId, userId, fobId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/remote-lock/fobs`,
      { userId, fobId },
      { headers: { token } },
    )
  },
  revokeRemoteLockFob: (buildingId, userId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/remote-lock/fobs/unbind`,
      { userId },
      { headers: { token } },
    )
  },
  getRemoteLockBuildingFobs: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/building/${buildingId}/remote-lock/fobs`, {
      headers: { token },
    })
  },
  getRemoteLockUserAccess: (buildingId, userId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/remote-lock/user-access`,
      { userId },
      { headers: { token } },
    )
  },
  updateRemoteLockUserAccess: (buildingId, userId, lockIds) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/remote-lock/user-access`,
      { userId, lockIds },
      { headers: { token } },
    )
  },
  provideRemoteLockStaffAccess: (buildingId, userId, startDate, endDate) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/remote-lock/staff`,
      { userId, startDate, endDate },
      { headers: { token } },
    )
  },

  getRemoteLockUserConnection: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/remote-lock/user-connection`,
      { headers: { token } },
    )
  },

  getRemoteLockGuestConnection: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/remote-lock/guest-connection`,
      { headers: { token } },
    )
  },

  getIglooWorkBuildingDevices: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/igloowork/building/${buildingId}/devices`, {
      headers: { token },
    })
  },
  createIglooWorkUser: (clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/igloowork/users`,
      { clientId },
      { headers: { token } },
    )
  },
  getIglooWorkUserDevices: (clientId) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/igloowork/${clientId}/devices`, {
      headers: { token },
    })
  },
  updateIglooWorkUserDevices: (clientId, devices, expirationDate) => {
    const token = getCookie('token')
    if (!token) throw Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/igloowork/${clientId}/devices`,
      { devices, expirationDate },
      { headers: { token } },
    )
  },

  getDeliveryAccessCodes: (buildingId, abortSignal) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/carrier?buildingId=${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
      signal: abortSignal,
    })
  },

  regenerateCarrierCode: (carrierId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.post(
      `${import.meta.env.VITE_API_URL}/carrier/${carrierId}/regenerate-code`,
      {},
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  generateParcelPendingWebhook: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.post(
      `${import.meta.env.VITE_API_URL}/parcelpendingwebhook/${buildingId}`,
      undefined,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getUserPaymentMethods: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/everyware/client`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },
  getMarketShopCategories: () => {
    const token = getTokenFromURL('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/market-shop/categories`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getPlaidLinkToken: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/dwolla/plaid/token`, {
      headers: { token },
    })
  },

  createFundingSource: (publicToken) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/api/dwolla/plaid/funding-sources`,
      { publicToken },
      { headers: { token } },
    )
  },

  getFundingSources: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/dwolla/funding-sources`, {
      headers: { token },
    })
  },

  getPackageRoomAccessCode: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.post(
      `${import.meta.env.VITE_API_URL}/package-room/code-by-employee`,
      { buildingId },
      { headers: { token } },
    )
  },

  getMarketShopProducts: ({ search, skip, limit, categoryId }) => {
    const token = getTokenFromURL('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/market-shop/products`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
      params: {
        search,
        skip,
        limit,
        categoryId,
      },
    })
  },

  updateCart: async (cartData) => {
    const token = getTokenFromURL('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/market-shop/cart`, cartData, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  proceedWithMarketShopPayment: async (sessionId) => {
    const token = getTokenFromURL('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/market-shop/pay`,
      { sessionId },
      { headers: { token } },
    )
  },

  getYardiTransactionsHistory: async ({ skip, limit }) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/yardi/client/transactions/history`, {
      headers: { token },
      params: { skip, limit },
    })
  },

  downloadMarketShopTransactionsCSV: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/market-shop/export-transactions?buildingId=${buildingId}`,
      {
        headers: {
          'content-type': 'text/plain;charset=utf-8',
          token: token,
        },
        responseType: 'blob',
      },
    )
  },

  downloadMarketShopInventoryCSV: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/market-shop/export-products?buildingId=${buildingId}`,
      {
        headers: {
          'content-type': 'text/plain;charset=utf-8',
          token: token,
        },
        responseType: 'blob',
      },
    )
  },

  uploadMarketShopProductsCSV: (buildingId, csvFile) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    const formData = new FormData()
    formData.append('buildingId', buildingId)
    formData.append('csv', csvFile)

    return axios.post(`${import.meta.env.VITE_API_URL}/market-shop/products`, formData, {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  getMarketShopLinks: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/market-shop/link?buildingId=${buildingId}`, {
      headers: { token },
    })
  },

  getBrivoClientConnectionData: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/brivo/building/${buildingId}/client`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  uploadAmenityAttachments: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/amenity/upload`, param(data), {
      headers: {
        'content-type': 'multipart/form-data',
        token: token,
      },
    })
  },

  getAmenityById: (amenityId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/amenity/${amenityId}/info`, {
      headers: { token: token },
    })
  },

  getAmenityMarketplace: (amenityId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/amenity-marketplace/${amenityId}`, {
      headers: { token: token },
    })
  },

  updateAmenityMarketplace: (amenityId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/amenity-marketplace/setup`,
      param({ ...data, amenityId }),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },

  getAmenityLocations: (amenityId, params = {}) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/amenity/${amenityId}/location`, {
      headers: { token: token },
      params,
    })
  },

  createAmenityLocation: (amenityId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/amenity/${amenityId}/location`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },

  updateAmenityLocation: (locationId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.put(
      `${import.meta.env.VITE_API_URL}/amenity/location/${locationId}`,
      param(data),
      {
        headers: {
          'content-type': 'multipart/form-data',
          token: token,
        },
      },
    )
  },

  deleteAmenityLocation: (locationId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.delete(`${import.meta.env.VITE_API_URL}/amenity/location/${locationId}`, {
      headers: { token: token },
    })
  },

  registerBuildingForAccentra: (buildingId, data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.post(
      `${
        import.meta.env.VITE_API_URL
      }/building/${buildingId}/accentra-integration/admin-credential`,
      data,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },

  getSaltospaceConnectionData: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/salto-space/integration/${buildingId}`, {
      headers: {
        'content-type': 'application/json',
        token: token,
      },
    })
  },

  getMyPermissions: () => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/me/permissions`, {
      headers: {
        'x-platform': 'web',
        token,
      },
    })
  },

  // Homepage Configuration API
  getHomepageConfig: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/homepage`, {
      headers: { token },
      params: { buildingId, newShortcutsSupported: true },
    })
  },

  updateHomepageConfig: (data) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(`${import.meta.env.VITE_API_URL}/homepage`, data, {
      headers: { token },
    })
  },

  getPackagesAnalytics: (buildingId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')

    return axios.get(`${import.meta.env.VITE_API_URL}/packages/building/${buildingId}/analytics`, {
      headers: { 'content-type': 'application/json', token },
    })
  },
  getEventById: (eventId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(`${import.meta.env.VITE_API_URL}/training/${eventId}`, {
      headers: { token },
    })
  },
  getPointCentralClientMapping: (buildingId, clientId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.get(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/point-central/client-mapping/${clientId}`,
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
  disconnectPointCentralResident: (buildingId, clientId) => {
    const token = getCookie('token')
    if (!token) throw new Error('Not Authorized')
    return axios.post(
      `${import.meta.env.VITE_API_URL}/building/${buildingId}/point-central/un-authorize`,
      { clientId },
      {
        headers: {
          'content-type': 'application/json',
          token: token,
        },
      },
    )
  },
}
